import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDeviceInfo } from '../hooks/useDeviceInfo';
import { useIpLocation } from '../hooks/useIpLocation';
import { sessionService } from '../services/sessionService';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const deviceInfo = useDeviceInfo();
  const { ipInfo } = useIpLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { user, error } = await signIn(email, password);
      
      if (error) throw error;
      
      // Registrar la información de la sesión
      if (user && deviceInfo) {
        try {
          const sessionData = {
            user_id: user.id,
            os_name: deviceInfo.osName,
            os_version: deviceInfo.osVersion,
            browser_name: deviceInfo.browserName,
            browser_version: deviceInfo.browserVersion,
            device_type: deviceInfo.deviceType,
            ip_address: ipInfo?.ip || '',
            location: ipInfo?.location ? {
              country: ipInfo.location.country,
              region: ipInfo.location.region,
              city: ipInfo.location.city,
              coordinates: {
                latitude: ipInfo.location.latitude,
                longitude: ipInfo.location.longitude
              }
            } : null,
            login_timestamp: new Date().toISOString(),
          };
          
          const result = await sessionService.createSessionLog(sessionData);
        } catch (sessionError: any) {
          console.error('Error al crear registro de sesión:', sessionError);
          setError('Error al registrar la sesión');
        }
      } else {
        console.log('No se pudo crear el registro de sesión:', { 
          hasUser: !!user, 
          hasDeviceInfo: !!deviceInfo,
          hasIpInfo: !!ipInfo
        });
      }

      navigate('/');
    } catch (err: any) {
      console.error('Error en el proceso de login:', err);
      setError(err.message || 'Ocurrió un error durante la autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Panel Izquierdo - Imagen/Diseño */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Imagen de fondo */}
        <img
          src="/login.jpg"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay con gradiente */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/90 to-blue-500/90" />
        
        {/* Contenido sobre la imagen */}
        <div className="relative w-full p-12 flex flex-col justify-between z-10">
          <div>
            <img
              src="/Logo.png"
              alt="Logo de la empresa"
              className="h-24 w-auto"
            />
            <h2 className="mt-6 text-4xl font-bold text-white">
              Control de Empleados
            </h2>
            <p className="mt-4 text-lg text-indigo-100">
              Sistema integral para la gestión y monitoreo de actividades laborales
            </p>
          </div>
          <div>
            <p className="text-indigo-100">
              2025 Integration IT SAS. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* Panel Derecho - Formulario */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img
              src="/Logo.png"
              alt="Logo de la empresa"
              className="h-20 w-auto mx-auto"
            />
          </div>
          
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Bienvenido de nuevo
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Ingresa tus credenciales para acceder al sistema
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Correo electrónico
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : 'Iniciar sesión'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
